import { useState, useEffect, useCallback } from 'react';
import { VisibilityState, Updater } from '@tanstack/react-table';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';

// Configuração de uma coluna
export interface ColumnConfig {
    id: string;
    label: string;
    category: string;
    defaultVisible: boolean;
    canHide: boolean;
}

// Configuração de um preset
export interface PresetConfig {
    id: string;
    label: string;
    description: string;
    columns: string[];
}

// Presets disponíveis
export const COLUMN_PRESETS: PresetConfig[] = [
    {
        id: 'cadastral_update',
        label: 'Atualização Cadastral',
        description: 'Foco no acompanhamento de atualizações de dados dos colaboradores',
        columns: [
            'name',
            'activityStatus',
            'dhoStatus',
            'dpStatus',
            'techStatus',
            'userStatus',
            'actions'
        ]
    },
    {
        id: 'logistics',
        label: 'Logística',
        description: 'Foco na gestão de endereços e localização dos colaboradores',
        columns: [
            'name',
            'addressCity',
            'addressState',
            'mainAddress',
            'mailingAddress',
            'phone',
            'actions'
        ]
    },
    {
        id: 'salary_management',
        label: 'Gestão de Remuneração',
        description: 'Foco na gestão salarial e progressão de carreira dos colaboradores',
        columns: [
            'name',
            'startDate',
            'lastSalaryWithIncrease',
            'lastSalaryType',
            'timeSinceLastRaise',
            'salaryProgressionComposite',
            'plannedSalaryIncrease',
            'lastExtraAmount',
            'actions'
        ]
    },
    {
        id: 'allocation_management',
        label: 'Alocação',
        description: 'Foco no gestão das alocações dos colaboradores',
        columns: [
            'name',
            'allocationStatus',
            'allocationSummary',
            'actions'
        ]
    }
];

// Configuração das colunas disponíveis
export const AVAILABLE_COLUMNS: ColumnConfig[] = [
    // Essenciais
    { id: 'name', label: 'Nome', category: 'Essencial', defaultVisible: true, canHide: false },
    { id: 'actions', label: 'Ações', category: 'Essencial', defaultVisible: true, canHide: false },
    
    // Status
    { id: 'activityStatus', label: 'Atividade', category: 'Status', defaultVisible: true, canHide: true },
    { id: 'consolidatedStatus', label: 'Status Consolidado', category: 'Status', defaultVisible: true, canHide: true },
    { id: 'dhoStatus', label: 'Status DHO', category: 'Status', defaultVisible: false, canHide: true },
    { id: 'dpStatus', label: 'Status DP', category: 'Status', defaultVisible: false, canHide: true },
    { id: 'techStatus', label: 'Status Tech', category: 'Status', defaultVisible: false, canHide: true },
    { id: 'userStatus', label: 'Status User', category: 'Status', defaultVisible: true, canHide: true },
    
    // Alocação
    { id: 'allocationStatus', label: 'Status de Alocação', category: 'Alocação', defaultVisible: false, canHide: true },
    { id: 'allocationSummary', label: 'Alocações Ativas', category: 'Alocação', defaultVisible: false, canHide: true },
    
    // Dados de Contato (Lote 1)
    { id: 'email', label: 'Email', category: 'Dados de Contato', defaultVisible: false, canHide: true },
    { id: 'phone', label: 'Telefone', category: 'Dados de Contato', defaultVisible: false, canHide: true },
    
    // Dados Profissionais (Lote 1)
    { id: 'positionName', label: 'Cargo', category: 'Dados Profissionais', defaultVisible: false, canHide: true },
    { id: 'departmentName', label: 'Departamento', category: 'Dados Profissionais', defaultVisible: false, canHide: true },
    
    // Localização (Lote 1)
    { id: 'addressCity', label: 'Cidade', category: 'Localização', defaultVisible: true, canHide: true },
    { id: 'addressState', label: 'Estado', category: 'Localização', defaultVisible: false, canHide: true },
    
    // Dados Pessoais (Lote 2)
    { id: 'birthDate', label: 'Data de Nascimento', category: 'Dados Pessoais', defaultVisible: false, canHide: true },
    { id: 'cpf', label: 'CPF', category: 'Dados Pessoais', defaultVisible: false, canHide: true },
    { id: 'sex', label: 'Sexo', category: 'Dados Pessoais', defaultVisible: false, canHide: true },
    
    // Dados Contratuais (Lote 2)
    { id: 'collaboratorType', label: 'Tipo de Colaborador', category: 'Dados Contratuais', defaultVisible: false, canHide: true },
    { id: 'startDate', label: 'Data de Início', category: 'Dados Contratuais', defaultVisible: false, canHide: true },
    { id: 'workSchedule', label: 'Carga Horária', category: 'Dados Contratuais', defaultVisible: false, canHide: true },
    
    // Dados Técnicos (Lote 2)
    { id: 'seniorityName', label: 'Senioridade', category: 'Dados Técnicos', defaultVisible: false, canHide: true },
    { id: 'rating', label: 'Rating', category: 'Dados Técnicos', defaultVisible: false, canHide: true },
    
    // Responsabilidades (Lote 2)
    { id: 'responsibleName', label: 'Responsável', category: 'Responsabilidades', defaultVisible: false, canHide: true },
    { id: 'clientResponsible', label: 'Cliente Responsável', category: 'Responsabilidades', defaultVisible: false, canHide: true },
    
    // Benefícios (Lote 3)
    { id: 'hasHealthInsurance', label: 'Plano de Saúde', category: 'Benefícios', defaultVisible: false, canHide: true },
    { id: 'hasCajuBenefit', label: 'Benefício Caju', category: 'Benefícios', defaultVisible: false, canHide: true },
    { id: 'hasWellhubBenefit', label: 'Benefício Wellhub', category: 'Benefícios', defaultVisible: false, canHide: true },
    { id: 'transportationBenefitAmount', label: 'Vale Transporte', category: 'Benefícios', defaultVisible: false, canHide: true },
    
    // Dados Específicos (Lote 3)
    { id: 'cnpj', label: 'CNPJ', category: 'Dados Específicos', defaultVisible: false, canHide: true },
    { id: 'companyName', label: 'Nome da Empresa', category: 'Dados Específicos', defaultVisible: false, canHide: true },
    { id: 'isRaroAcademyAlumnus', label: 'Ex-Raro Academy', category: 'Dados Específicos', defaultVisible: false, canHide: true },
    
    // Informações Familiares (Lote 3)
    { id: 'maritalStatus', label: 'Estado Civil', category: 'Informações Familiares', defaultVisible: false, canHide: true },
    { id: 'motherName', label: 'Nome da Mãe', category: 'Informações Familiares', defaultVisible: false, canHide: true },
    
    // Contato Adicional (Lote 3)
    { id: 'personalEmail', label: 'Email Pessoal', category: 'Contato Adicional', defaultVisible: false, canHide: true },
    
    // Dados de Estagiário (Lote 4)
    { id: 'internshipEducationalInstitution', label: 'Instituição de Ensino', category: 'Dados de Estagiário', defaultVisible: false, canHide: true },
    { id: 'internshipCourse', label: 'Curso', category: 'Dados de Estagiário', defaultVisible: false, canHide: true },
    { id: 'internshipEndDate', label: 'Data de Término do Estágio', category: 'Dados de Estagiário', defaultVisible: false, canHide: true },
    
    // Informações de Saúde (Lote 4)
    { id: 'healthInsuranceProvider', label: 'Operadora do Plano', category: 'Informações de Saúde', defaultVisible: false, canHide: true },
    { id: 'isPCD', label: 'É PCD', category: 'Informações de Saúde', defaultVisible: false, canHide: true },
    { id: 'esocialCode', label: 'Código eSocial', category: 'Informações de Saúde', defaultVisible: false, canHide: true },
    
    // Dados Pessoais Estendidos (Lote 4)
    { id: 'nationality', label: 'Nacionalidade', category: 'Dados Pessoais Estendidos', defaultVisible: false, canHide: true },
    { id: 'race', label: 'Raça/Cor', category: 'Dados Pessoais Estendidos', defaultVisible: false, canHide: true },
    
    // Dados Contratuais Estendidos (Lote 4)
    { id: 'endDate', label: 'Data de Fim', category: 'Dados Contratuais Estendidos', defaultVisible: false, canHide: true },
    { id: 'roleType', label: 'Tipo de Função', category: 'Dados Contratuais Estendidos', defaultVisible: false, canHide: true },
    
    // Benefícios Adicionais (Lote 5)
    { id: 'hasZeloBenefit', label: 'Benefício Zelo', category: 'Benefícios Adicionais', defaultVisible: false, canHide: true },
    { id: 'hasGuapecoBenefit', label: 'Benefício Guapeco', category: 'Benefícios Adicionais', defaultVisible: false, canHide: true },
    { id: 'healthInsuranceInclusionDate', label: 'Data de Inclusão no Plano', category: 'Benefícios Adicionais', defaultVisible: false, canHide: true },
    
    // Informações Familiares Estendidas (Lote 5)
    { id: 'fatherName', label: 'Nome do Pai', category: 'Informações Familiares Estendidas', defaultVisible: false, canHide: true },
    { id: 'spouseName', label: 'Nome do Cônjuge', category: 'Informações Familiares Estendidas', defaultVisible: false, canHide: true },
    { id: 'gender', label: 'Gênero', category: 'Informações Familiares Estendidas', defaultVisible: false, canHide: true },
    
    // Preferências Pessoais (Lote 5)
    { id: 'shirtSize', label: 'Tamanho da Camisa', category: 'Preferências Pessoais', defaultVisible: false, canHide: true },
    { id: 'hasOnboardingKit', label: 'Kit de Onboarding', category: 'Preferências Pessoais', defaultVisible: false, canHide: true },
    
    // Dados Contratuais Finais (Lote 5)
    { id: 'experienceEndDate', label: 'Data de Fim da Experiência', category: 'Dados Contratuais Finais', defaultVisible: false, canHide: true },
    { id: 'naturality', label: 'Naturalidade', category: 'Dados Contratuais Finais', defaultVisible: false, canHide: true },
    
    // Contadores de Relacionamentos (Lote 6)
    { id: 'raroFeedbacksCount', label: 'Feedbacks Internos', category: 'Contadores', defaultVisible: false, canHide: true },
    { id: 'clientFeedbacksCount', label: 'Feedbacks de Cliente', category: 'Contadores', defaultVisible: false, canHide: true },
    { id: 'occurrencesCount', label: 'Ocorrências', category: 'Contadores', defaultVisible: false, canHide: true },
    { id: 'childrenCount', label: 'Filhos', category: 'Contadores', defaultVisible: false, canHide: true },
    { id: 'healthDependentsCount', label: 'Dependentes do Plano', category: 'Contadores', defaultVisible: false, canHide: true },
    
    // Endereços Específicos (Lote 7)
    { id: 'mainAddress', label: 'Endereço Principal', category: 'Endereços', defaultVisible: false, canHide: true },
    { id: 'mailingAddress', label: 'Endereço de Correspondência', category: 'Endereços', defaultVisible: false, canHide: true },
    
    // Métricas Salariais (Lote 8)
    { id: 'lastSalary', label: 'Último Salário', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'salaryProgression', label: 'Progressão Salarial', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'timeSinceLastRaise', label: 'Tempo sem Aumento', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastRaisePercentage', label: '% do Último Aumento', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'totalSalaryEvolution', label: '% Evolução Salarial', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastSalaryType', label: 'CLT ou PJ', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastExtraAmount', label: 'Último Extra/Caju', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastSalaryDate', label: 'Data do Último Salário', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastSalaryReason', label: 'Motivo do Último Salário', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'lastSalaryObservations', label: 'Observações do Último Salário', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    
    // Campos Compostos de Remuneração
    { id: 'lastSalaryWithIncrease', label: 'Último Salário + % Aumento', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'salaryProgressionComposite', label: 'Progressão Completa', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    { id: 'plannedSalaryIncrease', label: 'Aumento Planejado', category: 'Métricas Salariais', defaultVisible: false, canHide: true },
    
    // Gestão de Férias (Lote 9)
    { id: 'vacationBalance', label: 'Saldo de Dias', category: 'Gestão de Férias', defaultVisible: false, canHide: true },
    { id: 'vacationEntitledFrom', label: 'Direito a partir de', category: 'Gestão de Férias', defaultVisible: false, canHide: true },
    { id: 'vacationConcessivePeriod', label: 'Limite para agendar', category: 'Gestão de Férias', defaultVisible: false, canHide: true },
    { id: 'vacationSituation', label: 'Situação', category: 'Gestão de Férias', defaultVisible: false, canHide: true },
    
    // Formação e Saúde (Lote 10)
    { id: 'highestEducationLevel', label: 'Maior Nível de Formação', category: 'Formação Acadêmica', defaultVisible: false, canHide: true },
    { id: 'mainEducationInstitution', label: 'Instituição Principal', category: 'Formação Acadêmica', defaultVisible: false, canHide: true },
    { id: 'lastMedicalCertificate', label: 'Último Atestado Médico', category: 'Informações de Saúde', defaultVisible: false, canHide: true },
    { id: 'totalMedicalDaysOff', label: 'Total de Dias Afastados', category: 'Informações de Saúde', defaultVisible: false, canHide: true },
    
    // Recursos Técnicos (Lote 11)
    { id: 'primarySkill', label: 'Habilidades', category: 'Recursos Técnicos', defaultVisible: false, canHide: true },
];

// Chave para localStorage
const STORAGE_KEY = 'collaborators-table-column-visibility';

interface UseColumnVisibilityReturn {
    columnVisibility: VisibilityState;
    setColumnVisibility: (updater: Updater<VisibilityState>) => void;
    applyPreset: (presetId: string) => void;
    toggleAllColumns: (visible: boolean) => void;
    getAvailableColumns: () => ColumnConfig[];
    getPresets: () => PresetConfig[];
    resetToDefault: () => void;
    detectActivePreset: () => string | null;
}

export const useColumnVisibility = (): UseColumnVisibilityReturn => {
    // SECURITY: Check salary permissions
    const { canViewSalary } = useSalaryPermissions();

    // Gerar estado inicial baseado na configuração padrão
    const getDefaultVisibility = useCallback((): VisibilityState => {
        const defaultState: VisibilityState = {};
        AVAILABLE_COLUMNS.forEach(column => {
            // SECURITY: Hide salary columns if user doesn't have permission
            if (column.category === 'Métricas Salariais' && !canViewSalary) {
                defaultState[column.id] = false;
            } else {
                defaultState[column.id] = column.defaultVisible;
            }
        });
        return defaultState;
    }, [canViewSalary]);

    // Estado da visibilidade das colunas
    const [columnVisibility, setColumnVisibilityState] = useState<VisibilityState>(() => {
        const defaults = getDefaultVisibility();
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    ...defaults,
                    ...parsed
                };
            }
        } catch (error) {
            console.warn('Erro ao carregar preferências de colunas do localStorage:', error);
        }
        return defaults;
    });

    // Salvar no localStorage sempre que mudar
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility));
        } catch (error) {
            console.warn('Erro ao salvar preferências de colunas no localStorage:', error);
        }
    }, [columnVisibility]);

    // Setter que atualiza o estado (compatível com TanStack Table)
    const setColumnVisibility = useCallback((updater: Updater<VisibilityState>) => {
        setColumnVisibilityState(prev => {
            const newValue = typeof updater === 'function' ? updater(prev) : updater;
            return newValue;
        });
    }, []);

    // Aplicar um preset específico
    const applyPreset = useCallback((presetId: string) => {
        const preset = COLUMN_PRESETS.find(p => p.id === presetId);
        if (!preset) {
            console.warn(`Preset "${presetId}" não encontrado`);
            return;
        }

        const newVisibility: VisibilityState = {};
        AVAILABLE_COLUMNS.forEach(column => {
            newVisibility[column.id] = preset.columns.includes(column.id);
        });

        setColumnVisibilityState(newVisibility);
    }, []);

    // Mostrar/ocultar todas as colunas (respeitando canHide)
    const toggleAllColumns = useCallback((visible: boolean) => {
        const newVisibility: VisibilityState = {};
        AVAILABLE_COLUMNS.forEach(column => {
            if (column.canHide) {
                newVisibility[column.id] = visible;
            } else {
                newVisibility[column.id] = true; // Colunas essenciais sempre visíveis
            }
        });
        setColumnVisibilityState(newVisibility);
    }, []);

    // Resetar para configuração padrão
    const resetToDefault = useCallback(() => {
        setColumnVisibilityState(getDefaultVisibility());
    }, [getDefaultVisibility]);

    // Detectar preset ativo baseado no estado atual das colunas
    const detectActivePreset = useCallback((): string | null => {
        // Para cada preset, verificar se as colunas visíveis correspondem exatamente
        for (const preset of COLUMN_PRESETS) {
            const presetMatches = AVAILABLE_COLUMNS.every(column => {
                const shouldBeVisible = preset.columns.includes(column.id);
                const isVisible = columnVisibility[column.id] !== false;
                return shouldBeVisible === isVisible;
            });
            
            if (presetMatches) {
                return preset.id;
            }
        }
        
        return null; // Nenhum preset corresponde ao estado atual
    }, [columnVisibility]);

    // Getters para configurações
    // SECURITY: Filter salary columns from available columns if user doesn't have permission
    const getAvailableColumns = useCallback(() => {
        if (canViewSalary) {
            return AVAILABLE_COLUMNS;
        }
        return AVAILABLE_COLUMNS.filter(col => col.category !== 'Métricas Salariais');
    }, [canViewSalary]);

    // SECURITY: Filter presets that include salary columns
    const getPresets = useCallback(() => {
        if (canViewSalary) {
            return COLUMN_PRESETS;
        }
        // Remove the salary management preset for users without permission
        return COLUMN_PRESETS.filter(preset => preset.id !== 'salary_management');
    }, [canViewSalary]);

    return {
        columnVisibility,
        setColumnVisibility,
        applyPreset,
        toggleAllColumns,
        getAvailableColumns,
        getPresets,
        resetToDefault,
        detectActivePreset,
    };
};
